import { useState } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { useCreateSettingsProfile } from "@/api/mutations/settingsProfiles";
import { RepoBranchPicker } from "@/components/gitea/RepoBranchPicker";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CreateSettingsProfileDialog({ open, onOpenChange }: Props) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [seedRepo, setSeedRepo] = useState<string | null>(null);
  const [seedBranch, setSeedBranch] = useState<string | null>(null);
  const [nameError, setNameError] = useState<string | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);
  const mutation = useCreateSettingsProfile();

  function reset() {
    setName("");
    setDescription("");
    setSeedRepo(null);
    setSeedBranch(null);
    setNameError(null);
    setServerError(null);
  }

  function handleClose(o: boolean) {
    if (!o) reset();
    onOpenChange(o);
  }

  function handleSubmit() {
    if (name.trim().length === 0) {
      setNameError("Pflichtfeld");
      return;
    }
    setNameError(null);
    setServerError(null);
    mutation.mutate(
      { name: name.trim(), description: description.trim(), seedRepo, seedBranch },
      {
        onSuccess: (p) => {
          toast.success(`Profil ${p.name} angelegt`);
          handleClose(false);
        },
        onError: (err) => {
          const msg = (err as Error).message;
          setServerError(msg);
          toast.error(`Create fehlgeschlagen: ${msg}`);
        },
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent data-testid="create-settings-profile-dialog">
        <DialogHeader>
          <DialogTitle>Neues Settings-Profil</DialogTitle>
          <DialogDescription>
            Name + Beschreibung anlegen. Die Config-Layer (managed / owner / mcpServers)
            pflegst du danach auf der Detail-Seite.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor="sp-name">Name</Label>
            <Input
              id="sp-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Standard"
              data-testid="sp-field-name"
              autoFocus
            />
            {nameError && <div className="text-xs text-red">{nameError}</div>}
          </div>
          <div className="space-y-1">
            <Label htmlFor="sp-description">Beschreibung</Label>
            <Textarea
              id="sp-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              data-testid="sp-field-description"
            />
          </div>
          <RepoBranchPicker
            seedRepo={seedRepo}
            seedBranch={seedBranch}
            onChange={(r, b) => {
              setSeedRepo(r);
              setSeedBranch(b);
            }}
          />
        </div>

        {serverError && (
          <div className="text-sm text-red" data-testid="sp-server-error">
            {serverError}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => handleClose(false)} data-testid="sp-cancel">
            Abbrechen
          </Button>
          <Button onClick={handleSubmit} disabled={mutation.isPending} data-testid="sp-submit">
            {mutation.isPending ? "..." : "Profil erstellen"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
